import React, {useState, useEffect} from 'react';
import {motion, AnimatePresence} from 'framer-motion';

const LoadingScreen = ({onLoadingComplete}) => {
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(timer);
          setIsComplete(true);
          setTimeout(() => {
            onLoadingComplete();
          }, 800);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 100);

    return () => clearInterval(timer);
  }, [onLoadingComplete]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{opacity: 1}}
        exit={{opacity: 0, scale: 1.1}}
        transition={{duration: 0.8, ease: "easeInOut"}}
        className="fixed inset-0 z-50 bg-gradient-to-br from-primary via-blue-600 to-blue-800 flex items-center justify-center"
      >
        <div className="text-center">
          <motion.div
            initial={{scale: 0.5, opacity: 0}}
            animate={{scale: 1, opacity: 1}}
            transition={{duration: 0.6, ease: "easeOut"}}
            className="mb-8"
          >
            {/* Light logo for dark blue gradient background */}
            <motion.img 
              src="https://i.ibb.co/39dmWcR7/Tutorhub-Logo-light.png" 
              alt="Tutor Hub Online" 
              className="h-16 lg:h-20 mx-auto mb-6"
              animate={{
                rotateY: [0, 10, -10, 0],
                scale: [1, 1.05, 1]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <motion.h2
              initial={{y: 20, opacity: 0}}
              animate={{y: 0, opacity: 1}}
              transition={{delay: 0.3, duration: 0.6}}
              className="text-2xl lg:text-3xl font-bold text-white mb-2"
            >
              Tutor Hub Online
            </motion.h2>
            <motion.p
              initial={{y: 20, opacity: 0}}
              animate={{y: 0, opacity: 1}}
              transition={{delay: 0.5, duration: 0.6}}
              className="text-blue-100 text-lg"
            >
              Loading your academic success...
            </motion.p>
          </motion.div>

          <motion.div
            initial={{width: 0}}
            animate={{width: "300px"}}
            transition={{delay: 0.7, duration: 0.5}}
            className="mx-auto mb-4"
          >
            <div className="bg-white/20 rounded-full h-3 overflow-hidden">
              <motion.div
                className="bg-accent h-full rounded-full"
                initial={{width: 0}}
                animate={{width: `${progress}%`}}
                transition={{duration: 0.3, ease: "easeOut"}}
              />
            </div>
          </motion.div>

          <motion.p
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            transition={{delay: 1, duration: 0.5}}
            className="text-blue-100 text-sm"
          >
            {Math.round(progress)}%
          </motion.p>

          {isComplete && (
            <motion.div
              initial={{scale: 0}}
              animate={{scale: 1}}
              className="mt-6"
            >
              <div className="w-16 h-16 mx-auto border-4 border-accent rounded-full flex items-center justify-center">
                <motion.svg
                  initial={{pathLength: 0}}
                  animate={{pathLength: 1}}
                  transition={{duration: 0.5}}
                  className="w-8 h-8 text-accent"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <motion.path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </motion.svg>
              </div>
            </motion.div>
          )}
        </div>

        <motion.div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{y: [0, -10, 0]}}
          transition={{repeat: Infinity, duration: 2}}
        >
          <div className="flex space-x-2">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-3 h-3 bg-accent rounded-full"
                animate={{scale: [1, 1.2, 1]}}
                transition={{repeat: Infinity, duration: 1.5, delay: i * 0.2}}
              />
            ))}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LoadingScreen;